// File: packages/ui/src/StatCard.tsx
import React from 'react';
import clsx from 'clsx';
import { Card } from './Card';
import { Tooltip } from './Tooltip';
import { renderIcon } from '@dopomogai/utils';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

export type StatCardProps = {
  title: string;
  value: string | number;
  // Back-compat field used across the repo
  description?: string;
  // New, more explicit tooltip text (falls back to description)
  tooltipText?: string;
  // Icon can be a ReactNode or a string name supported by renderIcon
  icon?: React.ReactNode | string;
  trend?: 'up' | 'down' | 'neutral';
  className?: string;
};

const trendIcons = {
  up: <ArrowUpRight className="text-emerald-500 [.theme-liquid-glass_&]:text-emerald-300" size={18} />,
  down: <ArrowDownRight className="text-rose-500 [.theme-liquid-glass_&]:text-rose-300" size={18} />,
  neutral: <Minus className="text-slate-400 [.theme-liquid-glass_&]:text-white/50" size={18} />,
};

export function StatCard({ title, value, icon, trend, tooltipText, description, className }: StatCardProps) {
  const tip = tooltipText ?? description;
  const content = (
    <Card noPadding className={clsx(
      // Neobrutalism
      'bg-slate-50',
      // Liquid glass
      '[.theme-liquid-glass_&]:bg-white/5',
      className
    )}>
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-2">
            <div className={clsx(
              'text-sm font-semibold',
              'text-slate-600 [.theme-liquid-glass_&]:text-white/70'
            )}>{title}</div>
            <div className={clsx(
              'text-2xl font-black',
              'text-slate-900 [.theme-liquid-glass_&]:text-white'
            )}>{String(value)}</div>
            {description && !tooltipText && (
              <div className="text-xs text-slate-500 [.theme-liquid-glass_&]:text-white/50">{description}</div>
            )}
          </div>
          {icon && (
            <div className={clsx(
              'flex items-center justify-center w-10 h-10 rounded-lg',
              // Neobrutalism
              'bg-white border-2 border-black',
              // Liquid glass
              '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20'
            )}>
              {renderIcon(icon, { size: 20, className: 'text-slate-800 [.theme-liquid-glass_&]:text-white' })}
            </div>
          )}
        </div>
        {trend && (
          <div className="flex items-center mt-2 text-xs text-slate-500 [.theme-liquid-glass_&]:text-white/60">
            {trendIcons[trend]}
            <span className="ml-1">vs. industry average</span>
          </div>
        )}
      </div>
    </Card>
  );

  return tip ? <Tooltip text={tip}>{content}</Tooltip> : content;
}
